import Vue from 'vue'
import Router, { isNavigationFailure, NavigationFailureType } from 'vue-router'

Vue.use(Router)

// Swallow expected vue-router 3.x NavigationFailures (redirected/cancelled/duplicated)
// so that guard-driven redirects (e.g. `next(fallback)` in beforeEach) don't surface as
// uncaught promise rejections in the console. Real errors still propagate.
const SUPPRESSED_NAV_FAILURES =
  NavigationFailureType.redirected |
  NavigationFailureType.cancelled |
  NavigationFailureType.duplicated

;['push', 'replace'].forEach((method) => {
  const original = Router.prototype[method]
  Router.prototype[method] = function (location, onResolve, onReject) {
    if (onResolve || onReject) {
      return original.call(this, location, onResolve, onReject)
    }
    return original.call(this, location).catch((err) => {
      // WARNING: Do not swallow all errors here!
      // We only swallow specific Vue Router 3 NavigationFailures.
      // Real errors (like a throwing nav guard or an invalid path
      // from handleSetSessionAndRedirectMessage) MUST be re-thrown
      // so they can be caught and handled properly by the application.
      if (isNavigationFailure(err, SUPPRESSED_NAV_FAILURES)) {
        return err
      }
      throw err
    })
  }
})

/* Layout */
import Layout from '@/layout'
import { launchdarklyKeys } from './utils/launchdarklyKeys'

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * redirect: noRedirect           if set noRedirect will no redirect in the breadcrumb
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
    roles: ['admin','editor']    control the page roles (you can set multiple roles)
    title: 'title'               the name show in sidebar and breadcrumb (recommend set)
    icon: 'svg-name'/'el-icon-x' the icon show in the sidebar
    breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
    activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
    sidebarFeatureFlagKey: 'featureFlagKey' if set, the sidebar will show if the feature flag is true
    featureFlagKey: 'featureFlagKey' if set, the sidebar will show if the feature flag is true
    featureFlagKeysAny: ['featureFlagKey1', 'featureFlagKey2'] if set, the sidebar will show if any of the feature flags are true
    featureFlagInvert: true if set, the sidebar will show if the feature flag is false
    featureFlagFallback: { path: '/path' } if set, the sidebar will redirect to the path if the feature flag is false
    featureFlagFallbackWhen: [{ flagKey: 'featureFlagKey', fallback: { path: '/path' } }] if set, the sidebar will redirect to the path if the feature flag is false
    legacySidebarTitle: 'Legacy Title' if set, the sidebar will show the legacy title if the feature flag is false
    messagingLegacySidebar: true if set, the sidebar will show the legacy sidebar if the feature flag is false
  }
 */

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */
export const constantRoutes = [
  {
    path: '/index.html',
    redirect: '/dashboard',
    hidden: true,
  },
  {
    path: '/',
    redirect: '/dashboard',
    hidden: true,
    meta: {
      useNpDashDashboard: launchdarklyKeys.useNpDashDashboard,
    },
  },
  {
    path: '/admin-login.html',
    name: 'adminLogin',
    component: () => import(/* webpackChunkName: "login" */ '@/components/Login/admin-login.vue'),
    hidden: true,
    meta: {
      requiresNoAuth: true,
    },
  },
  {
    path: '/reset.html',
    name: 'reset',
    component: () => import(/* webpackChunkName: "login" */ '@/components/Login/reset.vue'),
    hidden: true,
    meta: {
      requiresNoAuth: true,
    },
  },
  {
    path: '/password-reset.html',
    name: 'passwordReset',
    hidden: true,
    component: () =>
      import(/* webpackChunkName: "login" */ '@/components/Login/password-reset.vue'),
    meta: {
      requiresNoAuth: true,
    },
  },
  {
    path: '/dashboard',
    component: Layout,
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Home'),
        meta: {
          title: 'Home',
          icon: ['far', 'home-alt'],
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/email-builder',
    component: Layout,
    children: [
      {
        path: '',
        name: 'EmailBuilder',
        hidden: true,
        component: () =>
          import(/* webpackChunkName: "emails" */ '@/components/Emails/views/EmailPage'),
        meta: {
          title: 'Email',
          icon: ['far', 'home-alt'],
          requiresAuth: true,
        },
      },
    ],
    meta: {
      featureFlagKey: launchdarklyKeys.stripoEmailBuilderPages,
    },
  },
  {
    path: '/emails',
    name: 'Emails',
    hidden: true,
    component: () => import(/* webpackChunkName: "emails" */ '@/components/Emails/views/EmailPage'),
    meta: {
      title: 'Email',
      icon: ['far', 'home-alt'],
      requiresAuth: true,
      featureFlagKey: launchdarklyKeys.stripoEmailBuilderPages,
    },
  },
  {
    path: '/email',
    name: 'email',
    component: Layout,
    hidden: true,
    meta: {
      title: 'Email',
      icon: ['far', 'home-alt'],
      requiresAuth: true,
      featureFlagKey: launchdarklyKeys.npoFeatureEnabled,
    },
    children: [
      {
        path: '',
        name: 'Email',
        component: () => import(/* webpackChunkName: "emails" */ '@/components/Email'),
      },
    ],
  },
  {
    path: '/reports',
    component: Layout,
    // name: 'Reports',
    meta: {
      title: 'Reports',
      icon: ['far', 'analytics'],
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'reports',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Reports'),
        meta: {
          title: 'Analytics',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'donations',
        name: 'reportsDonation',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Reports/donations.vue'),
        meta: {
          title: 'Donations',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'payouts',
        name: 'reportsPayouts',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Reports/payouts'),
        meta: {
          title: 'Payouts',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'campaigns',
        name: 'reportsCampaigns',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Reports/campaigns'),
        meta: {
          title: 'Campaigns',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'monthly',
        name: 'reportsMonthly',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Reports/monthly'),
        meta: {
          title: 'Monthly',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'round-ups',
        name: 'reportsRoundUps',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Reports/round-ups.vue'),
        meta: {
          title: 'Round-Ups',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'tickets',
        name: 'reportsTickets',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Reports/tickets.vue'),
        meta: {
          title: 'Tickets',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'donation-notes',
        name: 'reportsDonationNotes',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Reports/donation-notes'),
        meta: {
          title: 'Notes',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
      {
        path: 'donation-in-honor',
        name: 'reportsInHonor',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Reports/donation-in-honor'),
        meta: {
          title: 'Tributes',
          requiresAuth: true,
          saveParams: ['start-date', 'end-date'],
        },
      },
    ],
  },

  {
    path: '/contacts',
    component: Layout,
    meta: {
      title: 'Contacts',
      icon: ['far', 'user'],
      requiresAuth: true,
      featureFlagKey: launchdarklyKeys.useNpDashContacts,
      featureFlagInvert: true,
      featureFlagFallback: { name: 'crmContacts' },
    },
    children: [
      {
        path: '',
        name: 'contacts',
        hidden: true,
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Contacts'),
        meta: {
          title: 'Contacts',
          requiresAuth: true,
        },
        props: {
          isList: false,
        },
      },
      {
        path: 'saved-contacts',
        name: 'savedContacts',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Contacts'),
        meta: {
          title: 'Saved Contacts',
          requiresAuth: true,
          saveParams: [],
        },
        props: {
          isList: false,
        },
      },
      {
        path: 'upload-contacts',
        name: 'uploadContacts',
        component: () =>
          import(/* webpackChunkName: "reports" */ '@/components/Contacts/UploadContacts'),
        meta: {
          title: 'Upload Contacts',
          requiresAuth: true,
          saveParams: [],
        },
      },
    ],
  },
  {
    path: '/crm',
    component: Layout,
    meta: {
      title: 'CRM',
      icon: ['far', 'user'],
      requiresAuth: true,
      featureFlagKeysAny: [launchdarklyKeys.useNpDashContacts],
    },
    children: [
      {
        path: '',
        name: 'crm',
        hidden: true,
        redirect: 'contacts',
      },
      {
        path: 'contacts',
        name: 'crmContacts',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Contacts'),
        meta: {
          title: 'Contacts',
          requiresAuth: true,
          featureFlagKey: launchdarklyKeys.useNpDashContacts,
          featureFlagFallback: { name: 'savedContacts' },
          refreshOnActiveClick: true,
        },
        props: {
          isList: false,
        },
      },
      {
        path: 'segments',
        name: 'crmSegments',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Segments'),
        meta: {
          title: 'Segments',
          requiresAuth: true,
          featureFlagKey: launchdarklyKeys.useNpDashSegments,
          featureFlagFallback: { name: 'Home' },
          refreshOnActiveClick: true,
        },
      },
    ],
  },
  {
    path: '/messaging',
    component: Layout,
    alwaysShow: true,
    redirect: '/messaging/email',
    meta: {
      title: 'Messaging',
      icon: ['far', 'comment-alt'],
      requiresAuth: true,
      featureFlagKey: launchdarklyKeys.useNpDashMessaging,
      featureFlagFallback: { name: 'messages' },
      sidebarFeatureFlagKey: launchdarklyKeys.useNpDashMessaging,
    },
    children: [
      {
        path: 'email',
        name: 'messagingEmail',
        component: () => import(/* webpackChunkName: "messaging" */ '@/components/Iframe.vue'),
        props: (route) => {
          if (route.query.new === 'true') return { route: '/email/edit' }
          const tab = Array.isArray(route.query.tab) ? route.query.tab[0] : route.query.tab
          if (tab === 'templates' || tab === 'designs' || tab === 'scheduled')
            return { route: `/email?tab=${tab}` }
          return { route: '/email' }
        },
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessagingEmail,
          featureFlagFallback: { name: 'messagingSms' },
          sidebarFeatureFlagKey: launchdarklyKeys.useNpDashMessagingEmail,
          title: 'Email',
          requiresAuth: true,
          refreshOnActiveClick: true,
        },
      },
      {
        path: 'sms',
        name: 'messagingSms',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/messages.vue'),
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'messages' },
          sidebarFeatureFlagKey: launchdarklyKeys.useNpDashMessagingSms,
          title: 'Text',
          requiresAuth: true,
          refreshOnActiveClick: true,
        },
      },
      {
        path: 'sms/outbox',
        name: 'messagingSmsOutbox',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/outbox.vue'),
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          //featureFlagFallback: { name: 'outbox' },
          hideFromSidebarWhenNpDashMessaging: true,
          title: 'Outbox',
          requiresAuth: true,
        },
      },
      {
        path: 'sms/templates',
        name: 'messagingSmsTemplates',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/templates.vue'),
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'templates' },
          hideFromSidebarWhenNpDashMessaging: true,
          title: 'Templates',
          requiresAuth: true,
        },
      },
      {
        path: 'sms/keyword',
        name: 'messagingSmsKeyword',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword.vue'),
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'keyword' },
          hideFromSidebarWhenNpDashMessaging: true,
          title: 'Keywords',
          requiresAuth: true,
        },
      },
      {
        path: 'sms/keyword/add',
        name: 'messagingSmsKeywordAdd',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword-edit.vue'),
        hidden: true,
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'addKeyword' },
          title: 'Add Keyword',
          requiresAuth: true,
        },
      },
      {
        path: 'sms/keyword/edit/:keyword',
        name: 'messagingSmsKeywordEdit',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword-edit.vue'),
        hidden: true,
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'editKeyword' },
          title: 'Edit Keyword',
          requiresAuth: true,
        },
      },
      {
        path: 'sms/auto-messages',
        name: 'messagingSmsAutoMessages',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/auto-messages.vue'),
        meta: {
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { name: 'AutoMessages' },
          hideFromSidebarWhenNpDashMessaging: true,
          title: 'Auto Messages',
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/messages',
    component: Layout,
    meta: {
      title: 'Messaging',
      icon: ['far', 'comment-alt'],
      requiresAuth: true,
      featureFlagInvert: true,
      featureFlagKey: launchdarklyKeys.useNpDashMessaging,
    },
    children: [
      {
        path: '',
        name: 'messages',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/messages.vue'),
        meta: {
          title: 'Messages',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
      {
        path: 'outbox',
        name: 'outbox',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/outbox.vue'),
        meta: {
          title: 'Outbox',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/outbox' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
      {
        path: 'lists',
        name: 'lists',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/lists.vue'),
        meta: {
          title: 'Lists',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/lists' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
      {
        path: 'templates',
        name: 'templates',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/templates.vue'),
        meta: {
          title: 'Templates',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/templates' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
      {
        path: 'keyword',
        name: 'keyword',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword.vue'),
        meta: {
          title: 'Keywords',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/keyword' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
      {
        path: 'keyword/add',
        name: 'addKeyword',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword-edit.vue'),
        hidden: true,
        meta: {
          title: 'Add Keyword',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/keyword/add' },
          requiresAuth: true,
        },
      },
      {
        path: 'keyword/edit/:keyword',
        name: 'editKeyword',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/keyword-edit.vue'),
        hidden: true,
        meta: {
          title: 'Edit Keyword',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/keyword/edit/:keyword' },
          requiresAuth: true,
        },
      },
      {
        path: 'auto-messages',
        name: 'AutoMessages',
        component: () =>
          import(/* webpackChunkName: "messages" */ '@/components/Messages/auto-messages.vue'),
        meta: {
          title: 'Auto Messages',
          featureFlagInvert: true,
          featureFlagKey: launchdarklyKeys.useNpDashMessaging,
          featureFlagFallback: { path: '/messaging/sms/auto-messages' },
          messagingLegacySidebar: true,
          requiresAuth: true,
        },
      },
    ],
  },

  {
    path: '/fundraise',
    component: Layout,
    name: 'fundraising',
    meta: {
      title: 'Fundraising',
      icon: ['far', 'book-heart'],
      requiresAuth: true,
    },
    children: [
      {
        path: 'add-a-donation',
        name: 'addADonation',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Fundraise/add-a-donation.vue'),
        meta: {
          title: 'Add a Donation',
          requiresAuth: true,
          featureFlagKey: launchdarklyKeys.showOfflineDonations,
        },
      },
      {
        path: 'giving-forms',
        // alias: '/fundraise/donation-asks',
        name: 'donationAsks',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Fundraise/donation-asks.vue'),
        meta: {
          title: 'Giving Forms',
          requiresAuth: true,
        },
      },
      {
        path: 'custom-fields',
        name: 'customFields',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Fundraise/custom-fields'),
        meta: {
          title: 'Custom Fields',
          requiresAuth: true,
        },
      },
      {
        path: 'impact-amounts',
        name: 'impactAmounts',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Fundraise/impact-amounts'),
        meta: {
          title: 'Impact Amounts',
          requiresAuth: true,
        },
      },
      {
        path: '/give-up',
        meta: {
          title: 'GiveUp to GiveBack',
          newWebAppLink: '/giveup-giveback',
        },
      },
      {
        path: '/donation-calculator',
        meta: {
          title: 'Donation Calculator',
          newWebAppLink: '/donation-calculator',
        },
      },
      {
        path: '/impact-calculator',
        meta: {
          title: 'Impact Calculator',
          newWebAppLink: '/impact-calculator',
        },
      },
      {
        path: '/organization',
        meta: {
          title: 'Organization',
          newWebAppLink: '/organization',
        },
      },
    ],
  },

  {
    path: '/campaigns',
    component: Layout,
    children: [
      {
        path: '',
        name: 'campaigns',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Campaigns/index.vue'),
        meta: {
          title: 'Campaigns',
          icon: ['far', 'usd-circle'],
          requiresAuth: true,
        },
      },
      {
        path: '/campaigns/:campaignId',
        name: 'campaignDetails',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Campaigns/details.vue'),
        hidden: true,
        meta: {
          title: 'Campaign Details',
          requiresAuth: true,
          activeMenu: '/campaigns',
        },
      },
      {
        path: '/campaign/edit/:campaignId',
        name: 'campaignEdit',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Campaigns/edit.vue'),
        hidden: true,
        meta: {
          requiresAuth: true,
          activeMenu: '/campaigns',
        },
      },
      {
        path: '/campaign/:campaignId',
        name: 'campaign',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Campaigns/campaign.vue'),
        hidden: true,
        meta: {
          requiresAuth: true,
          activeMenu: '/campaigns',
        },
      },
      {
        path: '/add-campaign',
        name: 'campaignAdd',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Campaigns/edit.vue'),
        hidden: true,
        props: {
          isAdd: true,
        },
        meta: {
          requiresAuth: true,
          activeMenu: '/campaigns',
        },
      },
    ],
  },
  {
    path: '/designs',
    component: Layout,
    children: [
      {
        path: '',
        name: 'designs',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Pages/designs.vue'),
        meta: {
          title: 'Designs',
          icon: ['far', 'image'],
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/events',
    component: Layout,
    children: [
      {
        path: '',
        name: 'events',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Events/events-list'),
        meta: {
          title: 'Events',
          icon: ['far', 'calendar-alt'],
          requiresAuth: true,
        },
      },
      {
        path: '/event/edit/:eventId',
        name: 'eventEdit',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Events/event-edit'),
        hidden: true,
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/add-event',
        name: 'eventAdd',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Events/event-edit'),
        hidden: true,
        meta: {
          requiresAuth: true,
          activeMenu: '/events',
        },
        props: {
          isAdd: true,
        },
      },
      {
        path: '/event/:eventId',
        name: 'event',
        component: () => import(/* webpackChunkName: "campaigns" */ '@/components/Events/event'),
        hidden: true,
        meta: {
          title: 'Event',
          requiresAuth: true,
          activeMenu: '/events',
        },
      },
      {
        path: '/event/:eventId/tickets',
        name: 'event-tickets',
        component: () =>
          import(/* webpackChunkName: "campaigns" */ '@/components/Events/event-ticket'),
        hidden: true,
        meta: {
          title: 'Event',
          requiresAuth: true,
          activeMenu: '/events',
        },
      },
      {
        path: '/event/:eventId/guests',
        name: 'event-guest',
        component: () => import('@/components/Events/event-guest'),
        hidden: true,
        meta: {
          title: 'Event Guests',
          requiresAuth: true,
          activeMenu: '/events',
        },
      },
    ],
  },
  {
    path: '/impact-posts',
    component: Layout,
    children: [
      {
        path: '',
        name: 'impactPosts',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/ImpactPosts/index.vue'),
        meta: {
          title: 'Posts',
          icon: ['far', 'pencil'],
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/settings',
    component: Layout,
    name: 'Settings',
    meta: {
      title: 'Settings',
      icon: ['far', 'cog'],
      requiresAuth: true,
    },
    children: [
      {
        path: 'account',
        name: 'settingsAccount',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/account.vue'),
        meta: {
          title: 'My Account',
          requiresAuth: true,
          noIntercom: true,
        },
      },
      {
        path: 'organization',
        name: 'settingsOrganization',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/organization.vue'),
        meta: {
          title: 'Organization',
          requiresAuth: true,
          noIntercom: true,
        },
      },
      {
        path: 'teammates',
        name: 'settingsTeammates',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/teammates.vue'),
        meta: {
          title: 'Teammates',
          requiresAuth: true,
        },
      },
      {
        path: 'email-domain',
        name: 'settingsEmailDomain',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/email-domain.vue'),
        meta: {
          title: 'Email Domain',
          requiresAuth: true,
          featureFlagKey: launchdarklyKeys.useNpDashEmailDomain,
          featureFlagFallback: { name: 'settingsAccount' },
        },
      },
      {
        path: 'stripe',
        name: 'settingsStripe',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Settings/stripe'),
        meta: {
          title: 'Stripe',
          requiresAuth: true,
        },
      },
      {
        path: 'billing',
        name: 'settingsBilling',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/billing.vue'),
        meta: {
          title: 'Billing',
          requiresAuth: true,
        },
      },
      {
        path: 'notifications',
        name: 'settingsNotifications',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/notifications.vue'),
        meta: {
          title: 'Notifications',
          requiresAuth: true,
          noIntercom: true,
        },
      },
      {
        path: 'website-code',
        name: 'settingsWebsiteCode',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/website-code.vue'),
        meta: {
          title: 'Website Code',
          requiresAuth: true,
        },
      },
      {
        path: 'integrations',
        name: 'settingsIntegrations',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/integrations.vue'),
        meta: {
          title: 'Integrations',
          requiresAuth: true,
        },
      },
      {
        path: 'import-export',
        name: 'settingsImportExport',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Settings/import-export.vue'),
        meta: {
          title: 'Import & Export',
          requiresAuth: true,
          featureFlagKey: launchdarklyKeys.useNpDashImportExport,
          featureFlagFallback: { name: 'settingsAccount' },
        },
      },
    ],
  },
  {
    path: '/integrations',
    component: Layout,
    children: [
      {
        path: '',
        name: 'integrations',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Pages/integrations.vue'),
        meta: {
          title: 'Integrations',
          icon: ['far', 'bolt'],
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/help-center',
    component: Layout,
    children: [
      {
        path: '',
        name: 'helpCenter',
        // eslint-disable-next-line no-unused-vars
        beforeEnter(to, from, next) {
          window.open('https://help.harnessgiving.com/', '_blank')
        },
        // component: () => import(/* webpackChunkName: "pages" */ '@/components/Pages/help-center.vue'),
        meta: {
          title: 'Help Center',
          icon: ['far', 'question-circle'],
          requiresAuth: true,
        },
      },
    ],
  },

  {
    path: '',
    component: Layout,
    children: [
      {
        path: '/refer',
        name: 'refer',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Pages/refer.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/messages/lists/:listId',
        name: 'ContactsList',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/Contacts'),
        props: {
          isList: true,
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/donate-button',
        name: 'donateButton',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Pages/donate-button.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/donate-button-widget',
        name: 'donateButtonWidget',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Pages/donate-button-widget.vue'),
        meta: {
          requiresAuth: true,
          noIntercom: true,
        },
      },
      {
        path: '/donate-button-widget-v2',
        name: 'donateButtonWidgetV2',
        component: () =>
          import(/* webpackChunkName: "pages" */ '@/components/Pages/donate-button-widget-v2.vue'),
        meta: {
          requiresAuth: true,
          noIntercom: true,
        },
      },
      {
        path: '/impact-post/:postId',
        name: 'impactPost',
        component: () => import('@/components/ImpactPosts/post.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/impact-post/edit/:postId',
        name: 'impactPostEdit',
        component: () => import('@/components/ImpactPosts/edit.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/add-impact-post',
        name: 'impactPostAdd',
        component: () => import('@/components/ImpactPosts/edit.vue'),
        props: {
          isAdd: true,
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/messages/new',
        name: 'messagesNew',
        component: () => import('@/components/Messages/new.vue'),
        props: {
          isTemplate: false,
          isNew: true,
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/templates/new',
        name: 'templatesNew',
        component: () => import('@/components/Messages/new.vue'),
        props: {
          isTemplate: true,
          isNew: true,
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/templates/edit/:templateId',
        name: 'templatesEdit',
        component: () => import('@/components/Messages/new.vue'),
        props: {
          isTemplate: true,
          isNew: false,
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/auto-messages/:msgId(\\d+)',
        name: 'AutoMessagesEdit',
        component: () => import('@/components/Messages/auto-messages-edit.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '*',
        name: '404',
        component: () => import(/* webpackChunkName: "pages" */ '@/components/Parts/NotFound'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
    hidden: true,
  },
]

const createRouter = () =>
  new Router({
    mode: 'history', // require service support
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRoutes,
  })

const router = createRouter()

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // reset router
}

export default router
